import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { db } from "../../services/firebase";
import { markMessageRead, deleteMessage } from "../../utils/messageService";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { ChevronDown, ChevronUp, Mail, Trash2, Trophy } from "lucide-react";

const MessagesPanel = () => {
	const { user } = useAuth();
	const [messages, setMessages] = useState([]);
	const [expandedMessages, setExpandedMessages] = useState(new Set());
	const [error, setError] = useState(null);
	const [deleteConfirm, setDeleteConfirm] = useState(null);
	const [deleteError, setDeleteError] = useState(null);

	// Set up real-time Firestore listener
	useEffect(() => {
		if (!user?.uid) return;

		const q = query(
			collection(db, "messages"),
			where("userId", "==", user.uid),
		);

		const unsubscribe = onSnapshot(
			q,
			(snapshot) => {
				const msgs = snapshot.docs.map((doc) => ({
					id: doc.id,
					...doc.data(),
				}));
				// Sort client-side by createdAt desc to avoid needing a composite index
				msgs.sort((a, b) => {
					const aTime = a.createdAt?.toMillis?.() || 0;
					const bTime = b.createdAt?.toMillis?.() || 0;
					return bTime - aTime;
				});
				// Limit to 50 most recent
				setMessages(msgs.slice(0, 50));
				setError(null);
			},
			(err) => {
				console.error("Messages listener error:", err);
				setError("Could not load messages");
				setMessages([]);
			},
		);

		return () => unsubscribe();
	}, [user?.uid]);

	// Toggle expand/collapse for a message
	const toggleMessage = async (messageId) => {
		setExpandedMessages((prev) => {
			const next = new Set(prev);
			if (next.has(messageId)) {
				next.delete(messageId);
			} else {
				next.add(messageId);
			}
			return next;
		});

		// Mark as read on expand
		const message = messages.find((m) => m.id === messageId);
		if (message && !message.read && !expandedMessages.has(messageId)) {
			try {
				await markMessageRead(messageId);
			} catch (err) {
				console.error("Failed to mark message as read:", err);
				// Retain unread styling — no state change needed since onSnapshot
				// will not update the read field if the write failed
			}
		}
	};

	// Handle delete button click — show confirmation
	const handleDeleteClick = (e, messageId) => {
		e.stopPropagation();
		setDeleteConfirm(messageId);
		setDeleteError(null);
	};

	// Confirm deletion
	const handleDeleteConfirm = async (messageId) => {
		try {
			await deleteMessage(messageId);
			setDeleteConfirm(null);
			setDeleteError(null);
		} catch (err) {
			console.error("Failed to delete message:", err);
			setDeleteError("Failed to delete message. Please try again.");
		}
	};

	// Cancel deletion
	const handleDeleteCancel = () => {
		setDeleteConfirm(null);
		setDeleteError(null);
	};

	// Calculate unread count
	const unreadCount = messages.filter((m) => m.read === false).length;

	// Format date from Firestore timestamp
	const formatDate = (timestamp) => {
		if (!timestamp) return "";
		const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
		return date.toLocaleDateString();
	};

	return (
		<div style={{ overflow: "hidden" }}>
			<h6 className="mb-3 d-flex align-items-center">
				<Mail size={18} className="me-2" style={{ color: "#d15a36" }} />
				Messages
				{unreadCount > 0 && (
					<span
						className="badge ms-2"
						style={{
							backgroundColor: "#d15a36",
							color: "#fff",
							fontSize: "0.7rem",
						}}
					>
						{unreadCount}
					</span>
				)}
			</h6>

			{error && (
				<div
					className="text-center py-3"
					style={{ color: "rgba(255,255,255,0.6)" }}
				>
					<p className="mb-0">{error}</p>
				</div>
			)}

			{!error && messages.length === 0 && (
				<div className="text-center py-5">
					<p className="text-muted">No messages available</p>
				</div>
			)}

			{!error && messages.length > 0 && (
				<div>
					{messages.map((message) => {
						const isExpanded = expandedMessages.has(message.id);
						const isUnread = message.read === false;

						return (
							<div
								key={message.id}
								className="mb-3"
								style={{
									borderRadius: "8px",
									overflow: "hidden",
									borderLeft: isUnread
										? "4px solid #d15a36"
										: "4px solid transparent",
									border: isExpanded ? "1px solid #d15a36" : "1px solid #333",
									borderLeftWidth: "4px",
									borderLeftColor: isUnread ? "#d15a36" : "transparent",
									backgroundColor: "#1a1a1a",
									transition: "border-color 0.2s ease",
								}}
							>
								{/* Header */}
								<div
									className="p-3"
									onClick={() => toggleMessage(message.id)}
									style={{
										cursor: "pointer",
										borderBottom: isExpanded ? "2px solid #d15a36" : "none",
									}}
								>
									<div className="d-flex justify-content-between align-items-start gap-2 mb-1">
										<div style={{ flex: 1, minWidth: 0 }}>
											<h6
												className="mb-0"
												style={{
													color: "#fff",
													wordBreak: "break-word",
													fontSize: "1rem",
													fontWeight: isUnread ? 700 : 400,
												}}
											>
												{message.type === "pb_congratulation" && (
													<Trophy
														size={16}
														className="me-2"
														style={{ color: "#d15a36" }}
													/>
												)}
												{message.title.replace(/🏆/g, "").trim()}
											</h6>
										</div>
										<div className="d-flex align-items-center flex-shrink-0">
											<button
												onClick={(e) => handleDeleteClick(e, message.id)}
												style={{
													background: "none",
													border: "none",
													cursor: "pointer",
													padding: "4px",
													marginRight: "4px",
													display: "flex",
													alignItems: "center",
												}}
												aria-label="Delete message"
											>
												<Trash2
													size={16}
													style={{ color: "rgba(255,255,255,0.4)" }}
												/>
											</button>
											{isExpanded ? (
												<ChevronUp size={18} style={{ color: "#d15a36" }} />
											) : (
												<ChevronDown
													size={18}
													style={{ color: "rgba(255,255,255,0.5)" }}
												/>
											)}
										</div>
									</div>

									{/* Meta row */}
									<div className="d-flex flex-wrap align-items-center gap-2">
										<span
											className="badge"
											style={{
												backgroundColor: "#d15a36",
												fontSize: "0.7rem",
											}}
										>
											{message.type === "pb_congratulation"
												? "PB"
												: message.type}
										</span>
										<small style={{ color: "rgba(255,255,255,0.45)" }}>
											{formatDate(message.createdAt)}
										</small>
										{isUnread && (
											<span
												style={{
													width: 8,
													height: 8,
													borderRadius: "50%",
													backgroundColor: "#d15a36",
													display: "inline-block",
												}}
											/>
										)}
									</div>
								</div>

								{/* Expanded content */}
								{isExpanded && (
									<div className="p-3" style={{ overflow: "hidden" }}>
										<p
											className="mb-0"
											style={{
												color: "rgba(255,255,255,0.7)",
												fontSize: "0.9rem",
												lineHeight: 1.6,
												wordBreak: "break-word",
											}}
										>
											{message.content}
										</p>
									</div>
								)}

								{/* Delete confirmation prompt */}
								{deleteConfirm === message.id && (
									<div
										className="p-3"
										style={{
											borderTop: "1px solid #333",
											backgroundColor: "#222",
										}}
									>
										<p
											className="mb-2"
											style={{
												color: "rgba(255,255,255,0.8)",
												fontSize: "0.85rem",
												margin: 0,
											}}
										>
											Delete this message?
										</p>
										{deleteError && (
											<p
												className="mb-2"
												style={{
													color: "#ff6b6b",
													fontSize: "0.8rem",
													margin: 0,
												}}
											>
												{deleteError}
											</p>
										)}
										<div className="d-flex gap-2">
											<button
												onClick={() => handleDeleteConfirm(message.id)}
												style={{
													backgroundColor: "#d15a36",
													color: "#fff",
													border: "none",
													borderRadius: "4px",
													padding: "4px 12px",
													fontSize: "0.8rem",
													cursor: "pointer",
												}}
											>
												Confirm
											</button>
											<button
												onClick={handleDeleteCancel}
												style={{
													backgroundColor: "transparent",
													color: "rgba(255,255,255,0.7)",
													border: "1px solid #555",
													borderRadius: "4px",
													padding: "4px 12px",
													fontSize: "0.8rem",
													cursor: "pointer",
												}}
											>
												Cancel
											</button>
										</div>
									</div>
								)}
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};

export default MessagesPanel;
